import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { DollarSign, ShoppingCart, TrendingUp, AlertTriangle, RefreshCw, LogOut } from "lucide-react";
import { toast } from "sonner";

const statusClass = {
  posted: "badge-posted", pending: "badge-pending", failed: "badge-failed",
  voided: "badge-voided", void_failed: "badge-void-failed", local_only: "badge-local-only"
};

export default function Dashboard() {
  const [stats, setStats] = useState({ today_revenue: 0, transaction_count: 0, average_order: 0, failed_postings: 0 });
  const [recentTxns, setRecentTxns] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [s, t, sess] = await Promise.all([
        api.get("/admin/dashboard/stats"),
        api.get("/admin/dashboard/recent-transactions"),
        api.get("/admin/sessions")
      ]);
      setStats(s.data);
      setRecentTxns(t.data);
      setSessions(sess.data);
    } catch (err) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); const iv = setInterval(fetchData, 30000); return () => clearInterval(iv); }, [fetchData]);

  const retryAll = async () => {
    try {
      await api.post("/admin/dashboard/retry-all");
      toast.success("Retry initiated");
      fetchData();
    } catch { toast.error("Retry failed"); }
  };

  const forceLogout = async (sessionId) => {
    try {
      await api.delete(`/admin/sessions/${sessionId}`);
      toast.success("Session terminated");
      fetchData();
    } catch { toast.error("Failed to terminate session"); }
  };

  const statCards = [
    { label: "Today's Revenue", value: `$${stats.today_revenue.toFixed(2)}`, icon: DollarSign, accent: "border-l-green-500" },
    { label: "Transactions", value: stats.transaction_count, icon: ShoppingCart, accent: "border-l-blue-500" },
    { label: "Avg Order Value", value: `$${stats.average_order.toFixed(2)}`, icon: TrendingUp, accent: "border-l-gray-400" },
    { label: "Failed Postings", value: stats.failed_postings, icon: AlertTriangle, accent: "border-l-red-500" },
  ];

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-dashboard">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children" data-testid="stats-cards">
        {statCards.map((card) => (
          <div key={card.label} className={`bg-white border border-gray-200 p-5 border-l-4 ${card.accent} animate-fade-in`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">{card.label}</p>
                <p className="font-heading text-2xl font-black tracking-tight mt-1">{card.value}</p>
              </div>
              <card.icon className="w-5 h-5 text-gray-300" aria-hidden="true" />
            </div>
          </div>
        ))}
      </div>

      {/* Failed Postings Alert */}
      {stats.failed_postings > 0 && (
        <div className="bg-red-50 border border-red-200 p-4 flex items-center justify-between" data-testid="failed-alert">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <span className="text-sm font-medium text-red-800">{stats.failed_postings} failed posting(s) require attention</span>
          </div>
          <Button onClick={retryAll} size="sm" className="rounded-none bg-red-500 hover:bg-red-600 text-white h-8 text-xs" data-testid="retry-all-button">
            <RefreshCw className="w-3 h-3 mr-1" /> Retry All
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Transactions */}
        <div className="lg:col-span-2 bg-white border border-gray-200" data-testid="recent-transactions">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400">Recent Transactions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-dense">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left">Ref</th><th className="text-left">Room</th>
                  <th className="text-left">Operator</th><th className="text-right">Amount</th><th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentTxns.slice(0, 15).map((txn) => (
                  <tr key={txn.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="font-mono text-xs">{txn.transaction_ref}</td>
                    <td className="font-semibold">{txn.room_number}</td>
                    <td className="text-gray-600">{txn.operator_name}</td>
                    <td className="text-right font-mono font-medium">${txn.grand_total?.toFixed(2)}</td>
                    <td className="text-center">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase ${statusClass[txn.status] || ''}`}>
                        {txn.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {recentTxns.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-8 text-gray-400 text-sm">No transactions today</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Active Sessions */}
        <div className="bg-white border border-gray-200" data-testid="active-sessions">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400">Active Sessions</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {sessions.map((s) => (
              <div key={s.id} className="px-4 py-3 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold">{s.display_name}</p>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">{s.role}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => forceLogout(s.id)}
                    className="h-7 text-[10px] text-red-400 hover:text-red-600 hover:bg-red-50"
                    data-testid={`force-logout-${s.id}`}>
                    <LogOut className="w-3 h-3" />
                  </Button>
                </div>
                <div className="mt-1 grid grid-cols-2 gap-1 text-[10px] text-gray-400">
                  <span>ID: {s.device_cookie_id?.slice(0, 8) || '—'}</span>
                  <span>{s.ip_address}</span>
                  <span className="col-span-2 truncate">{s.device_summary}</span>
                </div>
              </div>
            ))}
            {sessions.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-400 text-sm">No active sessions</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
